import { NgTemplateOutlet } from '@angular/common';
import { Component, Injector, computed, inject, untracked } from '@angular/core';
import { Router, RouterLink, UrlTree } from '@angular/router';
import { LucideModule } from '../../../../shared/modules/lucide.module';
import { slugDeServico } from '../../../../shared/utils/slug.util';
import { LinksDinamicosComponent } from '../../links-dinamicos/links-dinamicos.component';
import { LOCAL_HOME } from '../../links-dinamicos/links-dinamicos.model';
import { LinksDinamicosService } from '../../links-dinamicos/links-dinamicos.service';
import {
  AcessoRapidoPublicoService,
  DestaqueRapidoPublico,
} from '../../services/acesso-rapido-publico.service';
import { CategoriasPublicasService } from '../../services/categorias-publicas.service';
import { DESTAQUES_PADRAO, DestaquePadrao } from './destaques-padrao';
import { ehDestinoDeCategoria, linkDoDestaque } from './destino-destaque';

/** Um card da seção, na mesma forma para o padrão e para o personalizado. */
interface CardAcessoRapido {
  chave: string;
  titulo: string;
  descricao: string;
  /** Nome do ícone Lucide, ou `null` quando o ícone é a `imagem`. */
  icone: string | null;
  imagem: string | null;
  /** Cor do ponto no canto; `null` deixa o gradiente do tema. */
  cor: string | null;
  /** Página do portal; `null` no link externo. */
  destino: UrlTree | null;
  /** Endereço externo, aberto em nova aba; `null` na página do portal. */
  href: string | null;
}

/**
 * A seção Acesso rápido da homepage: os destaques que o órgão escolheu no adm e, depois deles,
 * os links dinâmicos publicados em "Home/Acesso Rápido".
 *
 * - Origem padrão (ou sem escolha gravada, ou leitura com erro): os cinco do sistema.
 * - Origem personalizada: os destaques do órgão cujo destino tem página no portal — e, sem
 *   nenhum, nenhum destaque, sem cair no padrão (decisão da usuária de 26/10/2026).
 * - Carregando: nada, para não mostrar o padrão e trocá-lo pelo personalizado em seguida.
 *
 * A seção inteira some quando não há destaque nem link dinâmico para mostrar.
 */
@Component({
  selector: 'app-links-acesso-rapido',
  templateUrl: './links-acesso-rapido.component.html',
  styleUrls: ['./links-acesso-rapido.component.css'],
  imports: [RouterLink, NgTemplateOutlet, LucideModule, LinksDinamicosComponent],
})
export class LinksAcessoRapidoComponent {
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly acessoRapido = inject(AcessoRapidoPublicoService);
  private readonly linksDinamicos = inject(LinksDinamicosService);

  /**
   * As categorias só entram quando algum destaque aponta para uma: pedir o serviço carrega o
   * catálogo público, e a maioria dos órgãos não usa esse destino.
   */
  private readonly usaCategoria = computed(() =>
    this.acessoRapido.destaques().some((destaque) => ehDestinoDeCategoria(destaque.destino))
  );

  private readonly slugsDeCategoria = computed<ReadonlySet<string>>(() => {
    if (!this.usaCategoria()) return new Set<string>();

    const categorias = this.categoriasPublicas().categorias();

    return new Set(categorias.map((categoria) => slugDeServico(categoria.titulo)));
  });

  private readonly carregando = computed(
    () =>
      this.acessoRapido.carregando() ||
      (this.usaCategoria() && this.categoriasPublicas().carregando())
  );

  readonly cards = computed<readonly CardAcessoRapido[]>(() => {
    if (this.carregando()) return [];

    if (!this.acessoRapido.personalizado()) {
      return this.destaquesPadrao.map((item) => this.doPadrao(item));
    }

    const slugs = this.slugsDeCategoria();

    return this.acessoRapido
      .destaques()
      .map((destaque) => this.doPersonalizado(destaque, slugs))
      .filter((card): card is CardAcessoRapido => card !== null);
  });

  readonly visivel = computed(
    () =>
      !this.carregando() &&
      (this.cards().length > 0 || this.linksDinamicos.porLocal(LOCAL_HOME).length > 0)
  );

  /** Por getter: um `const` importado pode nascer `undefined` num campo na suíte de testes. */
  get destaquesPadrao(): readonly DestaquePadrao[] {
    return DESTAQUES_PADRAO;
  }

  /**
   * Fora do contexto reativo: na primeira vez, o serviço nasce aqui e cria o `httpResource` do
   * catálogo, que o Angular não deixa criar dentro de um `computed` (NG0602).
   */
  private categoriasPublicas(): CategoriasPublicasService {
    return untracked(() => this.injector.get(CategoriasPublicasService));
  }

  private doPadrao(item: DestaquePadrao): CardAcessoRapido {
    return {
      chave: `padrao:${item.id}`,
      titulo: item.titulo,
      descricao: item.descricao,
      icone: item.icone,
      imagem: null,
      cor: item.cor,
      destino: this.router.parseUrl(item.caminho),
      href: null,
    };
  }

  private doPersonalizado(
    destaque: DestaqueRapidoPublico,
    slugs: ReadonlySet<string>
  ): CardAcessoRapido | null {
    const link = linkDoDestaque(destaque.tipoDestino, destaque.destino, slugs);

    if (!link) return null;

    return {
      chave: `destaque:${destaque.id}`,
      titulo: destaque.titulo,
      descricao: destaque.descricao,
      icone: destaque.icone,
      imagem: destaque.imagem,
      cor: destaque.cor,
      destino: link.interno ? this.router.parseUrl(link.caminho) : null,
      href: link.interno ? null : link.url,
    };
  }
}
